export const CHECKED = "June 2026";

export const COLUMNS = [
  { key: "trackr", name: "Trackr" },
  { key: "spreadsheet", name: "Spreadsheets" },
  { key: "huntr", name: "Huntr" },
  { key: "teal", name: "Teal" },
  { key: "notion", name: "Notion template" },
];

// true = yes, false = no, string = nuance
export const ROWS = [
  { label: "Paid plan price", trackr: "$6/mo or $19 once", spreadsheet: "Free", huntr: "From $40/mo", teal: "Teal+ $29/mo", notion: "Free (Notion AI extra)" },
  { label: "Free plan", trackr: "25 applications", spreadsheet: "Unlimited", huntr: "Up to 100 jobs", teal: "Yes", notion: "Yes" },
  { label: "One-time lifetime option", trackr: true, spreadsheet: "n/a", huntr: false, teal: false, notion: "n/a" },
  { label: "Ready-made job fields (status, stage, pay, contacts)", trackr: true, spreadsheet: "Build it yourself", huntr: true, teal: true, notion: "Depends on template" },
  { label: "Kanban pipeline board", trackr: true, spreadsheet: false, huntr: true, teal: true, notion: true },
  { label: "Follow-up nudges + weekly email digest", trackr: true, spreadsheet: false, huntr: "Check plan", teal: "Check plan", notion: false },
  { label: "AI job-description parsing", trackr: "Pro", spreadsheet: false, huntr: true, teal: true, notion: false },
  { label: "AI cover letters", trackr: "Pro", spreadsheet: false, huntr: "Limited on free", teal: "Limited on free", notion: false },
  { label: "Export to CSV, Excel and PDF", trackr: true, spreadsheet: true, huntr: "Partial", teal: "Partial", notion: "CSV / PDF" },
  { label: "Resume builder", trackr: false, spreadsheet: false, huntr: true, teal: true, notion: false },
  { label: "Chrome job-saving extension", trackr: false, spreadsheet: false, huntr: true, teal: true, notion: "Web clipper" },
];

export const RIVALS = {
  spreadsheet: {
    slug: "trackr-vs-spreadsheet",
    name: "Spreadsheets",
    long: "Google Sheets & Excel",
    title: "Trackr vs. a Job Application Spreadsheet (Google Sheets & Excel)",
    description: "Is a spreadsheet enough to track job applications? An honest comparison of Google Sheets and Excel with Trackr, a free job application tracker.",
    verdict: "A spreadsheet is a great free start. Trackr gives you the same flexibility, with the structure, reminders and exports already built, so you stop maintaining columns and start landing interviews.",
    trackrWins: [
      "No setup: every useful field (status, stage, pay, recruiter, URL, notes) is ready on day one.",
      "A Kanban board shows your whole pipeline at a glance, which is hard to do in rows and columns.",
      "Follow-up nudges and a Monday digest email remind you when to chase replies.",
      "Still export everything to CSV, Excel or PDF, so you're never locked in.",
    ],
    rivalWins: [
      "Completely free with no application limit.",
      "Infinitely customizable if you love building formulas and pivot tables.",
    ],
    faq: [
      ["Can I import my existing spreadsheet into Trackr?", "Yes. Trackr imports CSV files, so you can export your Google Sheet or Excel file as CSV and bring every application over in one go."],
      ["Is Trackr free like a spreadsheet?", "Trackr's free plan covers up to 25 applications with CSV export. Pro is $6/month or a one-time $19 for life."],
      ["Will I lose my data if I stop using Trackr?", "No. You can export your full list to CSV, Excel or PDF any time."],
    ],
  },
  huntr: {
    slug: "trackr-vs-huntr",
    name: "Huntr",
    long: "Huntr",
    title: "Trackr vs. Huntr: A Simpler, Cheaper Job Application Tracker",
    description: "Comparing Trackr and Huntr on price, features and simplicity. See which job tracker fits your search, and why many choose Trackr's $19 lifetime plan.",
    verdict: "Huntr is a powerful, resume-centric suite. Trackr focuses on doing job tracking really well for a fraction of the price: $6/month or $19 once, versus Huntr Pro from $40/month.",
    trackrWins: [
      "Far cheaper: $6/month or a one-time $19 lifetime unlock, compared with Huntr Pro from $40/month.",
      "AI mock interview prep with STAR starter answers for every role you save.",
      "Weekly digest email and follow-up nudges keep your search moving.",
      "Clean exports of your whole search to CSV, Excel and PDF.",
    ],
    rivalWins: [
      "A full resume builder with job-tailored resumes.",
      "A Chrome extension to clip jobs straight from job boards.",
      "A more generous free plan for tracking (up to 100 jobs).",
    ],
    faq: [
      ["Is Trackr cheaper than Huntr?", "Yes. Trackr Pro is $6/month or $19 one-time. Huntr's Pro plan is listed from $40/month (with discounts on longer billing periods)."],
      ["Does Trackr have a resume builder like Huntr?", "Not yet. Trackr stores the resume and cover letter you sent with each application, and generates AI cover letters, but it isn't a resume builder."],
      ["Can I move from Huntr to Trackr?", "Yes. Export your jobs from Huntr as CSV and import the file into Trackr."],
    ],
  },
  teal: {
    slug: "trackr-vs-teal",
    name: "Teal",
    long: "Teal",
    title: "Trackr vs. Teal: Which Job Tracker Is Right for You?",
    description: "An honest Trackr vs. Teal comparison covering pricing, job tracking, AI tools and exports, so you can pick the right job search tracker.",
    verdict: "Teal is a strong resume-and-tracking platform with Teal+ at $29/month. Trackr is the focused, affordable alternative: excellent tracking, AI helpers and exports for $6/month or $19 once.",
    trackrWins: [
      "Pay once ($19) and own Pro for life, with no recurring Teal+ subscription.",
      "AI mock interview prep built around the exact role you saved.",
      "Excel and PDF exports that look good enough to share.",
      "A calm, distraction-free dashboard focused on your pipeline.",
    ],
    rivalWins: [
      "A full resume builder with keyword matching and match scores.",
      "A Chrome extension for saving jobs from boards.",
    ],
    faq: [
      ["Is Trackr a Teal alternative?", "Yes. If you mainly need to track applications, follow up and prep for interviews, Trackr covers that for far less."],
      ["How much is Teal+ compared with Trackr Pro?", "Teal lists Teal+ at about $29/month (with weekly and quarterly options). Trackr Pro is $6/month or $19 one-time."],
      ["Can I use Teal's resume builder and Trackr together?", "Absolutely. Build resumes wherever you like and upload the final PDF to each application in Trackr's Documents locker."],
    ],
  },
  notion: {
    slug: "trackr-vs-notion",
    name: "Notion",
    long: "Notion job tracker templates",
    title: "Trackr vs. a Notion Job Tracker Template",
    description: "Notion job tracker template or a dedicated app? Compare a Notion job search board with Trackr's purpose-built application tracker.",
    verdict: "Notion templates look great and cost nothing, but you maintain them yourself. Trackr gives you the board and the brains: reminders, AI helpers, analytics and exports.",
    trackrWins: [
      "Purpose-built fields and statuses, with no template tinkering.",
      "Follow-up nudges and a Monday digest email (Notion won't remind you).",
      "AI job parsing, cover letters and interview prep included in Pro.",
      "Analytics on response rates by source.",
    ],
    rivalWins: [
      "Free, and lives alongside your other Notion notes.",
      "Fully customizable layouts and databases.",
    ],
    faq: [
      ["Is a Notion template good enough to track job applications?", "For a small search, yes. Once you pass a few dozen applications, reminders, analytics and exports start to matter, and that's where Trackr helps."],
      ["Can I import my Notion job board into Trackr?", "Yes. Export your Notion database as CSV, then import it into Trackr."],
      ["Does Trackr cost money?", "Trackr is free for up to 25 applications. Pro is $6/month or $19 one-time."],
    ],
  },
};

export const RIVAL_BY_SLUG = Object.fromEntries(Object.entries(RIVALS).map(([key, r]) => [r.slug, { ...r, key }]));
